import React from 'react';
import { useNavigate } from 'react-router-dom';

import { DetailsInfoContainer } from './ProfileWidget.styled';
import {
  ButtonsContainer,
  IconContainer,
  IllustratedBackgroundWidget,
  WidgetTitleContainer
} from '../Widgets/Widgets.styled';
import {
  CakeCandles,
  ClipboardUser,
  Envelope,
  House,
  Phone,
  Prescription
} from 'assets/icons/Icons';
import { Button } from 'components/v2/Buttons/Button/Button';
import { TextInput } from 'components/v2/Inputs/TextInput/TextInput';
import { Heading } from 'components/v2/Typography/Heading';
import { Patient } from 'kb-shared';
import { formatPhoneNumber } from 'kb-shared/utilities/phones';
import { analytics } from 'utilities/analytics';
import { formatDate } from 'utilities/formatDate';
import { pageUrl } from 'utilities/pageUrl';

import { DashboardWidgetList } from '../DashboardSettings/DashboardSettings.types';
import { isHiddenWidget } from '../DashboardSettings/DashboardSettings.utils';

interface Props {
  patient: Patient;
  hiddenWidgets?: DashboardWidgetList;
}

export const ProfileWidget = ({ patient, hiddenWidgets }: Props) => {
  const navigate = useNavigate();

  const birthdate = patient?.birthday ? formatDate(patient.birthday, 'MMMM DD, YYYY') : undefined;
  const patientPhone = formatPhoneNumber(patient.phone) || undefined;
  const pharmacyPhone = formatPhoneNumber(patient.patientPharmacy?.phone) || undefined;
  const { address, patientPharmacy } = patient;

  const addressDetails = [
    address?.address1,
    address?.address2,
    address?.city && address?.state ? `${address.city}, ${address.state}` : undefined,
    address?.zipcode
  ]
    .filter(Boolean)
    .join(', ');

  const pharmacyDetails = [patientPharmacy?.name, pharmacyPhone].filter(Boolean).join(', ');

  if (isHiddenWidget(hiddenWidgets, 'myProfile')) return null;

  return (
    <IllustratedBackgroundWidget variant={2}>
      <IconContainer $iconHeight={48} $iconWidth={36}>
        <ClipboardUser type="solid" />
      </IconContainer>
      <WidgetTitleContainer>
        <Heading tag="div" styledAs="h2">
          My Profile
        </Heading>
      </WidgetTitleContainer>
      <DetailsInfoContainer>
        {birthdate && (
          <TextInput
            ariaLabel="Birthdate"
            status="readOnly"
            type="text"
            value={birthdate}
            leftIcon={<CakeCandles type="regular" />}
          />
        )}
        {patient?.email && (
          <TextInput
            ariaLabel="Email"
            status="readOnly"
            type="text"
            value={patient?.email}
            leftIcon={<Envelope type="regular" />}
          />
        )}
        {patientPhone && (
          <TextInput
            ariaLabel="Phone"
            status="readOnly"
            type="text"
            value={patientPhone}
            leftIcon={<Phone type="regular" />}
          />
        )}
        {pharmacyDetails && (
          <TextInput
            ariaLabel="Pharmacy"
            status="readOnly"
            type="text"
            value={pharmacyDetails}
            leftIcon={<Prescription type="regular" />}
          />
        )}
        {addressDetails && (
          <TextInput
            ariaLabel="Address"
            status="readOnly"
            type="text"
            value={addressDetails}
            leftIcon={<House type="regular" />}
          />
        )}
      </DetailsInfoContainer>

      <ButtonsContainer>
        <Button
          label="Update Your Details"
          category="secondary"
          size="lg"
          onClick={() => {
            analytics.track(analytics.EVENTS.PROFILE_DETAILS_UPDATE_STARTED);
            navigate(pageUrl.myProfile());
          }}
        />
      </ButtonsContainer>
    </IllustratedBackgroundWidget>
  );
};
